/**
 * Geometry helpers for user markup edit / vertex handles.
 */

import { DRAW_TOOLS, measureUserGeometry, pdfPointToCanvas, pdfPointsToCanvas } from './userMarkup.js';
import {
  arcPdfVertices,
  buildArcGeometryFromVertices,
  buildCircleGeometryFromVertices,
  circlePdfVertices,
  isCircularArc,
  ARC_STYLES,
  normalizeArcGeometry,
  paintArcGeometry,
  paintCircleGeometry,
  translateArcGeometry,
  translateCircleGeometry,
  updateArcVertexAtIndex,
  updateCircleVertexAtIndex,
} from './arcGeometry.js';

function bboxFromPoints(points) {
  if (!points?.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return [x0, y0, x1 - x0, y1 - y0];
}

function circleBbox(center, radius) {
  return [center[0] - radius, center[1] - radius, 2 * radius, 2 * radius];
}

function isClosedPolygonGeometry(geom, component) {
  return geom?.type === 'polygon'
    && (geom.closed || component?.drawTool === DRAW_TOOLS.FREEFORM);
}

/** Open user polyline (not closed polygon) with at least two vertices. */
export function isOpenPolyline(component) {
  const geom = component?.geometry;
  if (!geom || geom.type === 'arc' || geom.type === 'circle' || geom.type === 'bbox') return false;
  if (isClosedPolygonGeometry(geom, component) || geom.closed) return false;
  return getComponentPdfVertices(component).length >= 2;
}

function angleBetweenPdfPointsDeg(a, b, c) {
  const v1x = a[0] - b[0];
  const v1y = a[1] - b[1];
  const v2x = c[0] - b[0];
  const v2y = c[1] - b[1];
  const len1 = Math.hypot(v1x, v1y);
  const len2 = Math.hypot(v2x, v2y);
  if (len1 < 1e-6 || len2 < 1e-6) return null;
  const cos = (v1x * v2x + v1y * v2y) / (len1 * len2);
  return (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI;
}

function rectangleFourthCornerPdf(a, b, c) {
  return [a[0] + c[0] - b[0], a[1] + c[1] - b[1]];
}

function pointInCanvasTriangle(px, py, t0, t1, t2) {
  const v0 = (t2.x - t0.x) * (py - t0.y) - (t2.y - t0.y) * (px - t0.x);
  const v1 = (t0.x - t1.x) * (py - t1.y) - (t0.y - t1.y) * (px - t1.x);
  const v2 = (t1.x - t2.x) * (py - t2.y) - (t1.y - t2.y) * (px - t2.x);
  const hasNeg = v0 < 0 || v1 < 0 || v2 < 0;
  const hasPos = v0 > 0 || v1 > 0 || v2 > 0;
  return !(hasNeg && hasPos);
}

/**
 * L-shaped open polyline (3 verts, ~90° at middle) → implied rectangle corner.
 * @returns {{ pointsPdf: number[][], fourthPdf: number[], cornerIndex: number } | null}
 */
export function getRightAngleRectangleCompletion(component, { angleToleranceDeg = 12 } = {}) {
  if (!isOpenPolyline(component)) return null;
  const pts = getComponentPdfVertices(component);
  if (pts.length !== 3) return null;

  const angle = angleBetweenPdfPointsDeg(pts[0], pts[1], pts[2]);
  if (angle == null || Math.abs(angle - 90) > angleToleranceDeg) return null;

  const fourthPdf = rectangleFourthCornerPdf(pts[0], pts[1], pts[2]);
  return {
    pointsPdf: [...pts, fourthPdf],
    fourthPdf,
    cornerIndex: 1,
  };
}

export function isPointInRectangleCompletionZone(canvasX, canvasY, component, viewport) {
  const completion = getRightAngleRectangleCompletion(component);
  if (!completion || !viewport) return false;

  const canvasVerts = getComponentCanvasVertices(component, viewport);
  const [a, , c] = canvasVerts;
  const d = pdfPointToCanvas(completion.fourthPdf[0], completion.fourthPdf[1], viewport);

  return pointInCanvasTriangle(canvasX, canvasY, a, d, c);
}

/** Add fourth corner and close an L-shaped open polyline into a rectangle polygon. */
export function completeOpenPolylineToRectangle(component, { scaleRatio = null } = {}) {
  const completion = getRightAngleRectangleCompletion(component);
  if (!completion) return component;

  const nextGeometry = {
    ...(component.geometry || {}),
    type: 'polygon',
    points: completion.pointsPdf,
    closed: true,
    bbox: bboxFromPoints(completion.pointsPdf),
  };
  const drawTool = component.drawTool === DRAW_TOOLS.POLYLINE
    ? DRAW_TOOLS.POLYGON
    : component.drawTool;
  return finalizeGeometry({ ...component, drawTool }, nextGeometry, { scaleRatio });
}

function finalizeGeometry(component, nextGeometry, { scaleRatio = null } = {}) {
  const geom = { ...nextGeometry };
  if (geom.type === 'circle' && geom.center && geom.radius) {
    geom.bbox = circleBbox(geom.center, geom.radius);
  } else if (geom.type === 'arc') {
    const verts = arcPdfVertices(geom);
    geom.bbox = bboxFromPoints(verts);
  } else if (geom.points?.length) {
    geom.bbox = bboxFromPoints(geom.points);
  }
  return {
    ...component,
    geometry: geom,
    bbox: geom.bbox,
    measurements: measureUserGeometry(geom, { scaleRatio }),
  };
}

/** PDF-space vertices for user markup or extracted IR component. */
export function getComponentPdfVertices(component) {
  if (!component) return [];
  const geomVerts = getMarkupPdfVertices(component);
  if (geomVerts.length) return geomVerts;
  if (Array.isArray(component.bbox) && component.bbox.length >= 4) {
    const [x, y, w, h] = component.bbox;
    return [[x, y], [x + w, y], [x + w, y + h], [x, y + h]];
  }
  return [];
}

export function getComponentCanvasVertices(component, viewport) {
  return pdfPointsToCanvas(getComponentPdfVertices(component), viewport);
}

export function componentHasEditableVertices(component) {
  return getComponentPdfVertices(component).length >= 2;
}

export function componentSupportsVertexAddRemove(component) {
  const geom = component?.geometry;
  if (!geom) return false;
  if (geom.type === 'arc' || geom.type === 'circle') return false;
  return getComponentPdfVertices(component).length >= 2;
}

function minVertexCount(component) {
  const geom = component?.geometry || {};
  const closed = isClosedPolygonGeometry(geom, component) || geom.type === 'bbox';
  return closed ? 3 : 2;
}

function pointToSegmentDist(px, py, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - a.x, py - a.y);
  let t = ((px - a.x) * dx + (py - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(px - projX, py - projY);
}

export function hitTestComponentEdge(canvasX, canvasY, component, viewport, threshold = 10) {
  if (!componentSupportsVertexAddRemove(component)) return -1;
  const verts = getComponentCanvasVertices(component, viewport);
  if (verts.length < 2) return -1;
  const geom = component.geometry || {};
  const closed = isClosedPolygonGeometry(geom, component) || geom.type === 'bbox';
  const segmentCount = closed ? verts.length : verts.length - 1;
  let bestEdge = -1;
  let bestDist = threshold;
  for (let i = 0; i < segmentCount; i += 1) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const d = pointToSegmentDist(canvasX, canvasY, a, b);
    if (d <= bestDist) {
      bestDist = d;
      bestEdge = i;
    }
  }
  return bestEdge;
}

export function insertVertexOnEdge(component, edgeIndex, pdfPoint, { scaleRatio = null } = {}) {
  if (!component || edgeIndex < 0 || !pdfPoint) return component;
  const verts = [...getComponentPdfVertices(component)];
  if (edgeIndex >= verts.length) return component;
  verts.splice(edgeIndex + 1, 0, pdfPoint);
  return updateComponentGeometry(component, verts, { scaleRatio });
}

export function removeVertexAtIndex(component, vertexIndex, { scaleRatio = null } = {}) {
  if (!component || vertexIndex < 0) return component;
  if (!componentSupportsVertexAddRemove(component)) return component;
  const verts = getComponentPdfVertices(component);
  if (verts.length <= minVertexCount(component)) return component;
  const nextVerts = verts.filter((_, i) => i !== vertexIndex);
  return updateComponentGeometry(component, nextVerts, { scaleRatio });
}

export function translateComponentPdfVertices(component, dx, dy, { scaleRatio = null } = {}) {
  const geom = component?.geometry || {};
  if (geom.type === 'arc') {
    const nextGeometry = translateArcGeometry(geom, dx, dy);
    return finalizeGeometry(component, nextGeometry, { scaleRatio });
  }
  if (geom.type === 'circle') {
    const nextGeometry = translateCircleGeometry(geom, dx, dy);
    return finalizeGeometry(component, nextGeometry, { scaleRatio });
  }
  const verts = getComponentPdfVertices(component).map(([x, y]) => [x + dx, y + dy]);
  return updateComponentGeometry(component, verts, { scaleRatio });
}

export function updateComponentVertex(component, vertexIndex, newPdfPoint, { scaleRatio = null } = {}) {
  if (!component || vertexIndex < 0 || !newPdfPoint) return component;
  const geom = component.geometry || {};

  if (geom.type === 'arc') {
    const nextGeometry = updateArcVertexAtIndex(normalizeArcGeometry(geom), vertexIndex, newPdfPoint);
    return finalizeGeometry(component, nextGeometry, { scaleRatio });
  }

  if (geom.type === 'circle') {
    const nextGeometry = updateCircleVertexAtIndex(geom, vertexIndex, newPdfPoint);
    return finalizeGeometry(component, nextGeometry, { scaleRatio });
  }

  const verts = getComponentPdfVertices(component);
  if (vertexIndex >= verts.length) return component;
  const nextVerts = verts.map((v, i) => (i === vertexIndex ? newPdfPoint : v));
  return updateComponentGeometry(component, nextVerts, { scaleRatio });
}

export function updateComponentGeometry(component, pdfVertices, { scaleRatio = null } = {}) {
  if (!component || !pdfVertices?.length) return component;
  const geom = component.geometry || {};

  if (geom.type === 'arc' && pdfVertices.length >= 3) {
    const arcStyle = isCircularArc(geom) ? ARC_STYLES.CIRCULAR : ARC_STYLES.BULGE;
    const built = buildArcGeometryFromVertices(pdfVertices, { arcStyle });
    return finalizeGeometry(component, built, { scaleRatio });
  }

  if (geom.type === 'circle' && pdfVertices.length >= 2) {
    const built = buildCircleGeometryFromVertices(pdfVertices);
    return finalizeGeometry(component, built, { scaleRatio });
  }

  const preserveType = geom.type === 'bbox' ? 'polygon' : geom.type;
  const closed = isClosedPolygonGeometry(geom, component);
  let nextType = preserveType;
  if (!nextType || nextType === 'bbox') {
    nextType = pdfVertices.length >= 3 ? 'polygon' : 'polyline';
  }
  if (closed) nextType = 'polygon';

  const nextGeometry = {
    ...geom,
    type: nextType,
    points: pdfVertices,
    closed: closed || (nextType === 'polygon' && geom.closed),
    bbox: bboxFromPoints(pdfVertices),
  };

  return finalizeGeometry(component, nextGeometry, { scaleRatio });
}

export function hitTestComponentVertex(canvasX, canvasY, component, viewport, radius = 8) {
  const verts = getComponentCanvasVertices(component, viewport);
  for (let i = verts.length - 1; i >= 0; i -= 1) {
    const v = verts[i];
    if (Math.hypot(canvasX - v.x, canvasY - v.y) <= radius) {
      return i;
    }
  }
  return -1;
}

/** PDF-space vertices for a user markup component. */
export function getMarkupPdfVertices(component) {
  const geom = component?.geometry;
  if (!geom) return [];
  if (geom.type === 'arc') return arcPdfVertices(geom);
  if (geom.type === 'circle') return circlePdfVertices(geom);
  if (geom.type === 'bbox' && Array.isArray(geom.bbox) && geom.bbox.length >= 4) {
    const [x, y, w, h] = geom.bbox;
    return [
      [x, y],
      [x + w, y],
      [x + w, y + h],
      [x, y + h],
    ];
  }
  return (geom.points || []).map((pt) => (Array.isArray(pt) ? [pt[0], pt[1]] : pt));
}

export function getMarkupCanvasVertices(component, viewport) {
  return pdfPointsToCanvas(getMarkupPdfVertices(component), viewport);
}

export function hitTestVertexHandle(canvasX, canvasY, component, viewport, radius = 8) {
  return hitTestComponentVertex(canvasX, canvasY, component, viewport, radius);
}

export function updateMarkupPdfVertices(component, pdfVertices, { scaleRatio = null } = {}) {
  return updateComponentGeometry(component, pdfVertices, { scaleRatio });
}

export function paintExtractedVertexHandles(ctx, component, viewport, opts = {}) {
  paintVertexHandles(ctx, component, viewport, opts);
}

export function paintVertexHandles(ctx, component, viewport, {
  activeIndex = -1,
  hoverIndex = -1,
} = {}) {
  if (!ctx || !viewport || !component) return;
  const verts = getComponentCanvasVertices(component, viewport);
  for (let i = 0; i < verts.length; i += 1) {
    const v = verts[i];
    const active = i === activeIndex;
    const hover = i === hoverIndex;
    const r = active ? 7 : hover ? 6 : 5;
    ctx.beginPath();
    ctx.arc(v.x, v.y, r, 0, Math.PI * 2);
    ctx.fillStyle = active ? '#2563eb' : '#ffffff';
    ctx.fill();
    ctx.strokeStyle = active || hover ? '#1d4ed8' : '#2563eb';
    ctx.lineWidth = 2;
    ctx.stroke();
  }
}

export function paintHoverMarkup(ctx, component, viewport) {
  if (!ctx || !viewport || !component?.geometry) return;
  const geometry = component.geometry;
  const stroke = 'rgba(37, 99, 235, 0.75)';

  if (geometry.type === 'arc') {
    paintArcGeometry(ctx, geometry, viewport, { stroke, width: 3, dash: [4, 4] });
    return;
  }

  if (geometry.type === 'circle') {
    paintCircleGeometry(ctx, geometry, viewport, { stroke, width: 3, dash: [4, 4] });
    return;
  }

  const verts = getMarkupCanvasVertices(component, viewport);
  if (verts.length < 2) return;
  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 3;
  ctx.setLineDash([4, 4]);
  ctx.beginPath();
  ctx.moveTo(verts[0].x, verts[0].y);
  for (let i = 1; i < verts.length; i += 1) {
    ctx.lineTo(verts[i].x, verts[i].y);
  }
  const closed = isClosedPolygonGeometry(geometry, component) || geometry.type === 'bbox';
  if (closed) ctx.closePath();
  ctx.stroke();
  ctx.restore();
}

/** Pick horizontal vs vertical ortho leg from cursor position relative to origin. */
export function resolveOrthoAxis(origin, pt) {
  const dx = Math.abs(pt.x - origin.x);
  const dy = Math.abs(pt.y - origin.y);
  return dx >= dy ? 'horizontal' : 'vertical';
}

/** Constrain point to horizontal or vertical from origin (ortho mode). */
export function applyOrthoLock(origin, pt, enabled, axis = null) {
  if (!enabled || !origin) return pt;
  const leg = axis || resolveOrthoAxis(origin, pt);
  if (leg === 'horizontal') return { x: pt.x, y: origin.y };
  return { x: origin.x, y: pt.y };
}

export { pdfPointToCanvas };
